import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { api, ApiError } from '@/lib/api';
import type { User } from '@merkos-mgmt/shared';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | undefined>();

  // Auto-refresh when past half-life of token
  useEffect(() => {
    if (!tokenExpiresAt) return;
    const now = Math.floor(Date.now() / 1000);
    const remaining = tokenExpiresAt - now;
    if (remaining <= 0) return;
    const halfLife = Math.max(remaining / 2, 60) * 1000;
    const timer = setTimeout(async () => {
      try {
        const data = await api<User>('/auth/me');
        if (data.token_expires_at) setTokenExpiresAt(data.token_expires_at);
      } catch { /* ignore — 401 handler redirects */ }
    }, halfLife);
    return () => clearTimeout(timer);
  }, [tokenExpiresAt]);

  const loadUser = useCallback(async () => {
    try {
      const data = await api<User>('/auth/me');
      setUser(data);
      if (data.token_expires_at) setTokenExpiresAt(data.token_expires_at);
    } catch (err) {
      const isAuthFailure = err instanceof ApiError && (err.status === 401 || err.status === 403);
      if (isAuthFailure) {
        try { await api('/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
      } else {
        setError('Failed to load user');
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, error, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
